import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, ShoppingCart, DollarSign, TrendingUp } from 'lucide-react';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { formatChartMonthKey } from '../../shared/utils/chartFormatters';
import {
  transformSalesData,
  groupSalesByProduct,
  groupSalesByPeriod,
} from '../../shared/utils/salesDataUtils';
import { isSalesAnalyticsDocument, normalizeDocType } from '../../shared/utils/documentAnalyticsFilters';
import { useDocuments } from '../../core/contexts/DocumentsContext';
import { useClients } from '../../core/contexts/ClientsContext';
import { useProducts } from '../../core/contexts/ProductsContext';
import { useSaudiDateFilter } from '../../products/saudi/context/SaudiDateFilterContext';
import { analyticsGroupTypeForRange, isDateInRange } from '../../products/saudi/utils/dateRangePresets';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const CONVERTED_TYPES = new Set(['facture', 'invoice']);
const CONVERTED_STATUSES = new Set(['completed', 'paid', 'delivered', 'payé', 'paye']);
const TREND_SLICE = 12;

function formatPeriodLabel(periodKey, groupType) {
  if (groupType === 'day' && ISO_DATE_RE.test(periodKey)) {
    return new Date(periodKey).toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' });
  }
  return formatChartMonthKey(periodKey, { fallback: periodKey }) || periodKey;
}

export default function AnalysesRapportsVentesPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.ventes.pages.analysesRapportsVentesPage.${key}`, opts);
  const { range } = useSaudiDateFilter();

  const { documents, refreshData, loading } = useDocuments();
  const { clients } = useClients();
  const { products } = useProducts();

  const salesData = useMemo(
    () => (documents || []).filter(isSalesAnalyticsDocument),
    [documents],
  );

  const transformedSales = useMemo(
    () => transformSalesData(salesData, clients),
    [salesData, clients],
  );

  const filteredSales = useMemo(
    () => transformedSales.filter((sale) => isDateInRange(sale.rawDate, range)),
    [transformedSales, range],
  );

  const groupType = useMemo(() => analyticsGroupTypeForRange(range), [range]);

  const salesByProduct = useMemo(
    () => groupSalesByProduct(filteredSales, products),
    [filteredSales, products],
  );

  const salesByPeriod = useMemo(
    () => groupSalesByPeriod(filteredSales, groupType),
    [filteredSales, groupType],
  );

  const totals = useMemo(() => {
    const totalQuantity = salesByProduct.reduce((sum, item) => sum + (item.quantitySold || 0), 0);
    const totalAmount = salesByProduct.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
    const saleCount = filteredSales.length;
    const avgBasket = saleCount > 0 ? totalAmount / saleCount : 0;
    return { totalQuantity, totalAmount, avgBasket, saleCount };
  }, [salesByProduct, filteredSales.length]);

  const conversionSplit = useMemo(() => {
    let converted = 0;
    filteredSales.forEach((sale) => {
      const type = normalizeDocType(sale.__raw?.type || sale.type);
      const status = String(sale.status || '').toLowerCase();
      if (CONVERTED_TYPES.has(type) || CONVERTED_STATUSES.has(status)) {
        converted += 1;
      }
    });
    return { converted, pending: Math.max(0, filteredSales.length - converted) };
  }, [filteredSales]);

  const findProductCode = useCallback(
    (name) => {
      const match = products.find((pr) => pr.name === name);
      return match?.reference || match?.code || '—';
    },
    [products],
  );

  const bestPeriod = useMemo(() => {
    if (!salesByPeriod.length) return null;
    return salesByPeriod.reduce((best, row) =>
      (row.totalAmount || 0) > (best.totalAmount || 0) ? row : best,
    salesByPeriod[0]);
  }, [salesByPeriod]);

  const topProduct = salesByProduct[0] || null;

  const insights = useMemo(
    () => [
      {
        label: p('insight_best_month'),
        value: bestPeriod
          ? formatPeriodLabel(bestPeriod.period, groupType)
          : '—',
        hint: bestPeriod ? formatBrandMoney(bestPeriod.totalAmount || 0) : undefined,
      },
      {
        label: p('insight_top_product'),
        value: topProduct?.product || '—',
        hint: topProduct ? formatBrandMoney(topProduct.totalAmount || 0) : undefined,
      },
    ],
    [bestPeriod, topProduct, groupType, p],
  );

  const stats = useMemo(
    () => [
      {
        label: p('kpi_revenue'),
        value: formatBrandMoney(totals.totalAmount),
        icon: DollarSign,
        variant: 'forest',
      },
      {
        label: p('kpi_unique_products'),
        value: salesByProduct.length,
        icon: Layers,
        variant: 'copper',
      },
      {
        label: p('kpi_avg_basket'),
        value: formatBrandMoney(totals.avgBasket),
        icon: TrendingUp,
        variant: 'forest',
      },
      {
        label: p('kpi_total_quantity'),
        value: totals.totalQuantity,
        icon: ShoppingCart,
        variant: 'copper',
      },
    ],
    [salesByProduct.length, totals, p],
  );

  const charts = useMemo(
    () => [
      {
        title: p('analysis_trends_title'),
        subtitle: p('chart_trends_subtitle'),
        variant: 'pipeline',
        rows: salesByPeriod.slice(-TREND_SLICE).map((row) => ({
          label: formatPeriodLabel(row.period, groupType),
          value: Number(row.totalAmount ?? 0),
          display: formatBrandMoney(row.totalAmount ?? 0),
        })),
      },
      {
        title: p('chart_top_products_title'),
        subtitle: p('chart_top_products_subtitle'),
        variant: 'ranked',
        rows: salesByProduct.slice(0, 8).map((row) => ({
          label: row.product,
          value: row.totalAmount || 0,
          display: formatBrandMoney(row.totalAmount || 0),
        })),
      },
      {
        title: p('chart_client_status_title'),
        subtitle: p('chart_client_status_subtitle'),
        variant: 'status',
        active: conversionSplit.converted,
        inactive: conversionSplit.pending,
      },
    ],
    [salesByPeriod, salesByProduct, conversionSplit, groupType, p],
  );

  const table = useMemo(
    () => ({
      title: p('report_articles_title'),
      columns: [
        { key: 'code', label: p('col_code') },
        { key: 'name', label: p('col_designation') },
        { key: 'quantity', label: p('col_qty_sold') },
        { key: 'unitPrice', label: p('col_unit_price') },
        { key: 'total', label: p('col_total_amount') },
      ],
      rows: salesByProduct.map((item) => ({
        code: findProductCode(item.product),
        name: item.product,
        quantity: String(item.quantitySold || 0),
        unitPrice: formatBrandMoney(item.averagePrice || 0),
        total: formatBrandMoney(item.totalAmount || 0),
      })),
    }),
    [salesByProduct, findProductCode, p],
  );

  const handleRefresh = useCallback(async () => {
    await refreshData?.();
  }, [refreshData]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={handleRefresh}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="ventes_analytics"
      emptyLabel={p('no_data')}
    />
  );
}
